import { Button, FormValues, useAppForm } from '@stern-ui/ui';
import { useDispatch, useSelector } from 'react-redux';
import z from 'zod';

import {
	resetFilterValues,
	saveFilterValues,
	selectFilterValues,
	setFilterWindowState,
} from '@/components/PatientDataTable';

import { dynamicFilterListType, formatFiltersTypes } from '../../../types';
import {
	formatAllFiltersForSaving,
	setupAllDynamicFilters,
	contactFiltersDefaultObj,
	surveyFiltersDefaultObj,
} from '../../../utils';

import { PatientListFiltersContact } from './PatientListFiltersContact';
import { patientListFiltersFormOptions } from './patientListFiltersFormOptions';
import { PatientListFiltersGeneral } from './PatientListFiltersGeneral';
import { PatientListFiltersLists } from './PatientListFiltersLists';
import { PatientListFiltersPatient } from './PatientListFiltersPatient';
import { PatientListFiltersSurvey } from './PatientListFiltersSurvey';

const ageMin = 0;
const ageMax = 120;

const optionValueSchema = z.object({
	name: z.string(),
	id: z.string(),
});

const dateOrEmptySchema = z
	.string()
	.refine(
		(value) => value === '' || /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value),
		'Invalid date format'
	);

const surveyFilterSchema = z
	.object({
		filter_type: z.string(),
		survey_id: optionValueSchema,
		survey_status: z.string(),
	})
	.superRefine((data, ctx) => {
		if (data.filter_type.length > 0 && !data.survey_id.id) {
			ctx.addIssue({
				code: 'custom',
				message: 'Survey name is required',
				path: ['survey_id'],
			});
		}
		if (data.filter_type.length > 0 && !data.survey_status) {
			ctx.addIssue({
				code: 'custom',
				message: 'Survey status is required',
				path: ['survey_status'],
			});
		}
	});

const contactFilterSchema = z
	.object({
		filter_type: z.string(),
		is_null: z.string(),
		name: z.string(),
	})
	.superRefine((data, ctx) => {
		if (data.is_null.length > 0 && !data.name) {
			ctx.addIssue({
				code: 'custom',
				message: 'Contact type is required',
				path: ['name'],
			});
		}
	});

const patientListFiltersSchema = z
	.object({
		study_id: z.array(optionValueSchema),
		criteria_include: z.array(optionValueSchema),
		criteria_exclude: z.array(optionValueSchema),
		hci_id: z.array(optionValueSchema),
		age: z.array(z.number()).length(2),
		gender: z.array(z.string()),
		legal_sex: z.array(z.string()),
		status: z.array(z.string()),
		patient_ids_include: z.array(z.string()),
		patient_ids_exclude: z.array(z.string()),
		needs_assistance: z.string(),
		survey_filters: z.array(surveyFilterSchema),
		contact_filters: z.array(contactFilterSchema),
		survey_needs_assistance: z.string(),
		survey_from_date: dateOrEmptySchema,
		survey_to_date: dateOrEmptySchema,
		person_lists_include: z.array(optionValueSchema),
		person_lists_exclude: z.array(optionValueSchema),
	})
	.superRefine((v, ctx) => {
		if (v.age) {
			const min = v.age[0];
			const max = v.age[1];
			if (min > max) {
				ctx.addIssue({
					code: 'custom',
					path: ['age'],
					message: 'Please ensure that minimum age is not greater than maximum age.',
				});
			}
			// validation for invalid values outside of the min and max range, or missing values
			// invalid/missing values will be set outside of the min/max range as a number value is stil required
			// essentially this makes the min/max inputs required fields and forces the user to enter valid values, rather than just leaving them blank
			if (min < ageMin || max > ageMax) {
				ctx.addIssue({
					code: 'custom',
					path: ['age'],
					message: 'Valid age value required for both minimum and maximum',
				});
			}
		}
	});

export const PatientListFilters = () => {
	const dispatch = useDispatch();
	const filterValues = useSelector(selectFilterValues);

	const dynamicFiltersList = [
		{
			name: 'survey_filters',
			defaultObj: surveyFiltersDefaultObj,
			validationKeyName: 'survey_status' as keyof formatFiltersTypes,
		},
		{
			name: 'contact_filters',
			defaultObj: contactFiltersDefaultObj,
			validationKeyName: 'name' as keyof formatFiltersTypes,
		},
	];
	const filterValuesFromState = setupAllDynamicFilters(
		filterValues as FormValues,
		dynamicFiltersList as dynamicFilterListType[]
	);
	const initialValues = {
		...patientListFiltersFormOptions.defaultValues,
		...filterValuesFromState,
	} as typeof patientListFiltersFormOptions.defaultValues;

	if (!initialValues.age || initialValues.age.length === 0) {
		initialValues.age = [ageMin, ageMax];
	}

	const form = useAppForm({
		defaultValues: initialValues,
		validators: {
			onMount: patientListFiltersSchema,
			onChange: patientListFiltersSchema,
		},
		onSubmit: ({ value }) => {
			const valuesForSave = { ...(value as FormValues) };
			const ageValues = value.age;
			if (
				Array.isArray(ageValues) &&
				ageValues.length === 2 &&
				ageValues[0] === ageMin &&
				ageValues[1] === ageMax
			) {
				valuesForSave.age = [];
			}
			const formattedFilterValues = formatAllFiltersForSaving(
				valuesForSave,
				dynamicFiltersList as dynamicFilterListType[]
			);
			dispatch(saveFilterValues({ filterValues: formattedFilterValues }));
			close();
		},
	});

	const close = () => {
		dispatch(setFilterWindowState({ filterWindowState: false }));
	};

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				void form.handleSubmit();
			}}
		>
			<div className='flex flex-col w-[90vw] max-w-full relative -mt-2'>
				<div className='max-h-[calc(100vh-230px)] overflow-y-auto mb-20 -ml-3 -mr-3 pr-3'>
					<PatientListFiltersGeneral form={form} />
					<PatientListFiltersPatient form={form} />
					<PatientListFiltersSurvey form={form} />
					<PatientListFiltersLists form={form} />
					<PatientListFiltersContact form={form} />
				</div>
				<div className='absolute left-0 bottom-0 w-full'>
					<div className='flex justify-between'>
						<form.AppForm>
							<form.Subscribe
								selector={(state) => ({
									isSubmitting: state.isSubmitting,
								})}
								children={({ isSubmitting }) => {
									return (
										<Button
											type='button'
											variant='tertiary'
											disabled={isSubmitting}
											onClick={() => {
												dispatch(resetFilterValues());
												form.reset(
													patientListFiltersFormOptions.defaultValues
												);
											}}
										>
											Clear
										</Button>
									);
								}}
							></form.Subscribe>
						</form.AppForm>
						<form.AppForm>
							<form.SubmitButton>Submit</form.SubmitButton>
						</form.AppForm>
					</div>
				</div>
			</div>
		</form>
	);
};
